import { Box, Paper, styled } from "@mui/material";
import { GetFieldLookup, useFormMetadata, useFormSave, useLookups, useSnackbar } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, Lot, LotStatuses, Permissions, SubStages } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import usePermissions from "../hooks/auth/usePermissions";
import useLotsByProjectIdStageId from "../hooks/data/useLotsByProjectIdStageId";
import PermissionsContext from "../contexts/auth/PermissionsContext";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));
const formatCurrency = (value: number) =>
    Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
    }).format(value);

export function normalizeLotDateOnMarket(value: string | undefined): string | undefined {
    if (!value) {
        return value;
    }

    const localDateTime = DateTime.fromISO(value);

    return localDateTime.isValid
        ? (localDateTime.toISO({
              includeOffset: false,
          }) ?? value)
        : value;
}

export function createLotDateOnMarketColumn(): AppColumn<Lot> {
    return {
        id: "dateOnMarket",
        label: "Release Date",
        scalarType: "date",
        dateSerialization: "localDateTime",
        accessor: (row) => normalizeLotDateOnMarket(row.dateOnMarket),
        formatter: (value) =>
            typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
        setValue: (row, value) => setLotField(row, "dateOnMarket", typeof value === "string" ? value : undefined),
        flex: 1,
    };
}

function setLotField<K extends keyof Lot>(row: Lot, key: K, value: Lot[K]): Lot {
    const updated = Object.assign(Object.create(Object.getPrototypeOf(row)) as Lot, row);

    updated[key] = value;

    return updated;
}

function errorMessages(reason: unknown): string[] {
    if (typeof reason !== "object" || reason === null) {
        return ["Unable to save lot."];
    }

    const responseStatus = (reason as { responseStatus?: { errors?: readonly { message?: string }[] } }).responseStatus;
    const messages = responseStatus?.errors
        ?.map((error) => error.message)
        .filter((message): message is string => Boolean(message));

    return messages?.length ? messages : ["Unable to save lot."];
}

const useLotColumns = (): readonly AppColumn<Lot>[] => {
    const metadata = useFormMetadata(FormDefinitions.Lot);
    const { data: permissions } = usePermissions();
    const { data: project } = useContext(ProjectContext);
    const { data: lookups } = useLookups(metadata);

    return useMemo(() => {
        const subStageOptions = (GetFieldLookup(lookups, "SubStage")?.items ?? []).flatMap((item) => {
            if (item.id == null || item.id === "") {
                return [];
            }

            const id = Number(item.id);

            return Number.isFinite(id)
                ? [
                      {
                          value: id,
                          label: item.value?.toString() ?? String(id),
                      },
                  ]
                : [];
        });
        const columns: AppColumn<Lot>[] = [
            {
                id: "number",
                label: "Lot Number",
                scalarType: "number",
                accessor: (row) => row.number,
                setValue: (row, value) => setLotField(row, "number", typeof value === "number" ? value : undefined),
                flex: 1,
            },
            {
                id: "advertisedPrice",
                label: "Price",
                scalarType: "number",
                accessor: (row) => row.advertisedPrice,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                setValue: (row, value) =>
                    setLotField(row, "advertisedPrice", typeof value === "number" ? value : undefined),
                canEdit: () => permissions?.includes(Permissions.LotCanUpdateAdvertisedPrice) ?? false,
                flex: 1,
            },
            createLotDateOnMarketColumn(),
            {
                id: "approvedRebate",
                label: "Rebate",
                scalarType: "number",
                accessor: (row) => row.approvedRebate,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                setValue: (row, value) =>
                    setLotField(row, "approvedRebate", typeof value === "number" ? value : undefined),
                flex: 1,
            },
            {
                id: "subStage",
                label: "SubStage",
                scalarType: "lookup",
                accessor: (row) => row.subStage,
                lookup: {
                    options: subStageOptions,
                },
                exportAccessor: ({ value }) => value,
                setValue: (row, value) =>
                    setLotField(row, "subStage", typeof value === "number" ? (value as SubStages) : undefined),
                flex: 1,
            },
        ];

        if (project?.marginScheme) {
            columns.splice(2, 0, {
                id: "landValue",
                label: "Land Value For Margin Scheme",
                scalarType: "number",
                accessor: (row) => row.landValue,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                setValue: (row, value) => setLotField(row, "landValue", typeof value === "number" ? value : undefined),
                flex: 1,
            });
        }

        return columns;
    }, [lookups, permissions, project]);
};

export const BulkEditLots: FC = () => {
    const { data: permissions } = useContext(PermissionsContext);
    const params = useParams();
    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);
    const lotMetadata = useFormMetadata(FormDefinitions.Lot);
    const save = useFormSave(lotMetadata);
    const { data: lots, loading: lotsLoading, refresh: lotsRefresh } = useLotsByProjectIdStageId(projectId, stageId);
    const snackbar = useSnackbar();
    const { data: project } = useContext(ProjectContext);
    const { data: stage } = useContext(StageContext);

    const addNewLot = async () => {
        const lot = new Lot({
            number: (lots?.reduce((current, item) => Math.max(current, item.number ?? 0), 0) ?? 0) + 1,
            status: LotStatuses.Available,
            stageId,
            advertisedPrice: 0,
            approvedRebate: 0,
        });
        const persisted = (await save(lot)) as Lot | undefined;

        await lotsRefresh();

        return persisted ?? lot;
    };

    const submit = async (draft: Lot, _original: Lot) => {
        try {
            const persisted = (await save(draft)) as Lot | undefined;

            await lotsRefresh();

            return persisted ?? draft;
        } catch (reason) {
            for (const message of errorMessages(reason))
                snackbar.enqueueSnackbar(message, {
                    variant: "error-critical",
                });
            throw reason;
        }
    };

    const columns = useLotColumns();
    const exportTitle = `${project?.name} - ${stage?.name} - Lots`;

    return (
        <>
            <Outlet />
            <TitleBox
                sx={{
                    display: "flex",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexGrow: 1,
                    }}
                >
                    {permissions?.includes(Permissions.StageCanAddLot) && (
                        <StyledButton variant="contained" onClick={addNewLot}>
                            Add Lot
                        </StyledButton>
                    )}
                </Box>
            </TitleBox>
            <StyledPaper>
                <CollapsibleAppTable
                    title="Lots"
                    startCollapsed={false}
                    loading={lotsLoading}
                    rows={lots ?? []}
                    columns={columns}
                    getRowId={(lot) => {
                        if (lot.id == null) {
                            throw new Error("Lot is missing its domain ID");
                        }

                        return lot.id;
                    }}
                    density="compact"
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    editing={{
                        mode: "row",
                        onCommit: submit,
                    }}
                    export={{
                        fileName: exportTitle,
                        exportTitle,
                        includeDateTime: true,
                    }}
                />
            </StyledPaper>
        </>
    );
};

export default BulkEditLots;
